import React from 'react';
import { useHistory } from 'react-router-dom';
import { CommandGroup, CommandItem } from '@dashboard/components/ui/command';
import { useRecents, type RecentType } from './useRecents';
import { useCommandPalette } from './CommandPaletteContext';
import { trackResultClick } from './telemetry';
import { useModifierOpen } from './useModifierOpen';
import { usePageScreenPath } from './usePageScreenPath';
import { actionEntries } from './registries/actions';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { User, CreditCard, RefreshCw, CalendarDays, Inbox, ShoppingCart } from 'lucide-react';

const RECENT_ICONS: Record<RecentType, React.ComponentType<{ className?: string }>> = {
  customer: User,
  payment: CreditCard,
  subscription: RefreshCw,
  booking: CalendarDays,
  submission: Inbox,
  'checkout-page': ShoppingCart,
};

export function PaletteEmptyState() {
  const { recents } = useRecents();
  const history = useHistory();
  const pageScreenPath = usePageScreenPath();
  const { logout } = useAuth();
  const { closePalette } = useCommandPalette();
  const modRef = useModifierOpen();

  const top5 = recents.slice(0, 5);

  return (
    <>
      {top5.length > 0 && (
        <CommandGroup heading="Recents">
          {top5.map((entry, idx) => {
            const Icon = RECENT_ICONS[entry.type];
            return (
              <CommandItem
                key={`${entry.type}-${entry.id}`}
                value={`${entry.label} ${entry.subtitle || ''}`}
                onSelect={() => {
                  trackResultClick({ group: 'recents', rank: idx, queryLength: 0 });
                  closePalette();
                  if (modRef.current) {
                    window.open(entry.path, '_blank', 'noopener,noreferrer');
                  } else {
                    history.push(
                      pageScreenPath(
                        entry.path,
                        entry.type === 'checkout-page' ? entry.subtitle : undefined,
                      ),
                    );
                  }
                }}
              >
                <Icon className="h-4 w-4" />
                <div className="flex min-w-0 items-baseline gap-2">
                  <span className="truncate">{entry.label}</span>
                  {entry.subtitle && (
                    <span className="truncate text-xs text-muted-foreground">{entry.subtitle}</span>
                  )}
                </div>
              </CommandItem>
            );
          })}
        </CommandGroup>
      )}
      <CommandGroup heading="Suggested actions">
        {actionEntries
          .filter(entry => !entry.hideFromSuggested)
          .map((entry, idx) => {
            const Icon = entry.icon;
            return (
              <CommandItem
                key={entry.id}
                value={[entry.label, ...(entry.keywords || [])].join(' ')}
                onSelect={() => {
                  trackResultClick({ group: 'actions', rank: idx, queryLength: 0 });
                  closePalette();
                  entry.run({ history, logout });
                }}
              >
                <Icon className="h-4 w-4" />
                <span>{entry.label}</span>
              </CommandItem>
            );
          })}
      </CommandGroup>
    </>
  );
}
